/**
 * SSI Clinical Review & Synthesis page (3_SSIReview.js).
 *
 * For organisations configured with ssiFlow.endPage = 'reviewAndSynthesis'
 * (e.g. The Owl Centre, SLaM/3 Borough), this page replaces BOTH the
 * Recommended Diagnosis Decision (3_RDD.js) and the SSI DF page (4_DF.js).
 *
 * Unlike the traditional RDD + DF flow, this page does NOT present:
 *   - DSM-5 Criteria panels (A-E)
 *   - A formal Recommended Diagnosis Decision
 *
 * Instead it presents:
 *   1. A read-only SSI Summary card (symptom counts + domain impairment)
 *   2. An "Emerging Differentials" multi-select panel (same as ConditionSelectionPanel)
 *   3. An "Emerging Formulation" free-text box (clinical reasoning notes)
 *   4. A "Confidential Notes" free-text box (private clinician notes)
 *
 * Data paths (dual-path architecture):
 *
 *   TOC / legacy (hasCIReview = false):
 *     - Differentials → dataModel.2_clinicalInterview.sections.12.differentialDiagnoses
 *     - Formulation  → dataModel.2_clinicalInterview.sections.12.clinicianNotes['Additional Notes']
 *     - Case Notes   → dataModel.3_semiStructuredInterview.sections.3.internalCaseNotes
 *     - DF status    → dataModel.2_clinicalInterview.sections.12.status
 *
 *   SLaM (hasCIReview = true):
 *     - Differentials panel hidden (Lead records those on CI Review)
 *     - Formulation  → emergingFormulations.ssiClinician.formulation
 *     - Case Notes   → emergingFormulations.ssiClinician.confidentialNotes
 *     - Confirmed    → emergingFormulations.ssiClinician.confirmed
 *     - Attribution  → emergingFormulations.ssiClinician.{updatedAt, updatedBy, updatedByName}
 *     CI section 12 is NOT touched — it belongs to the Lead Clinician's CI Review.
 */

import React, { useState, useMemo, useContext, useCallback, useEffect, useRef } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { ProformaNavigationGuardContext } from '../../../context/ProformaNavigationGuardContext';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { ChevronLeftIcon, ChevronRightIcon, CheckCircleIcon, XCircleIcon, SearchIcon, XIcon } from '@heroicons/react/solid';
import * as Dialog from '@radix-ui/react-dialog';
import Breadcrumb from '../../../components/Breadcrumb';
import { AccordionData } from '../../../constants/data_DF';
import ConditionSelectionPanelTOC from '../6_clinicalInterview/components/ConditionSelectionPanelTOC';
import DictationTextArea from '../../../components/DictationTextArea';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import ModuleCompletionDialog, { CancelButton, PrimaryActionButton, GenerateReportButton, SummarySection, DialogMessage } from '../../../components/ModuleCompletionDialog';
import InterviewFinishPathDialog from '../../../components/InterviewFinishPathDialog';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import { auth, db } from '../../../firebase';
import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { CI_FINISH_PATHS } from '../../../constants/workflowConfig';
import {
  buildDeferPadUpdates,
  buildPadInAppointmentUpdates,
} from '../../../utils/appointmentFinishPath';
import {
  isOriginalSSIClinician,
  isSSILeadOverrideActive,
  isLeadClinician,
} from '../../../utils/clinicianPermissions';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { useSectionExitRegeneration } from '../../../hooks/useSectionExitRegeneration';
import { buildFirstGeneratedFields, buildFirstCompletedFields, appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
import { isPatientFemale } from '../../../utils/conditionChecks';
import { ADHD_CONDITION_LABEL, getDiagnosticDecisionStartRoute } from '../../../utils/diagnosticDecisionRouting';
import { isDDCompleted } from '../../../utils/ssiConflictDetection';
import { recordLeadOverrideRecompletion } from '../../../utils/ssiOverrideAudit';
import ButtonSpinner from '../../../components/informantManagement/ButtonSpinner';
import SSIResetBanner from '../../../components/SSIResetBanner';
import SSIOverrideBanner from '../../../components/SSIOverrideBanner';
import SSIDecisionCountSummary from './components/SSIDecisionCountSummary';
import useSsiLockGuard from '../../../hooks/useSsiLockGuard';

export default function SSIReview() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patientDataModel, updatePatientFirestore, flushPendingUpdates } = useContext(PatientDataContext);
  const { claims } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);

  // One-clinician SLaM: bounce back to the overview while the SSI is locked
  // (awaiting the end-of-CI decision, or the assessment has been ended).
  useSsiLockGuard({ patientDataModel, wf, patientId });

  // One-clinician SLaM / 3 Borough: the Lead ran the CI and then this SSI in
  // the same appointment, so the SSI Review is the LAST interview of the day.
  // Instead of the plain completion dialog, Complete opens the appointment
  // finish wizard (share the outcome now and start the PAD, or end the
  // appointment and generate the report) — the same wizard two-clinician
  // SLaM patients see at the end of CI Review & Synthesis (12_DF.js).
  const asksAppointmentFinishPath = wf.appointmentFinishPathModule === 'ssi';

  // SLaM / 3 Borough (two-clinician hasCIReview, or one-clinician finish
  // wizard) and MDT-review orgs finish SSI by generating the SSI report and
  // going to Documents — the SSI clinician does not open DD from here.
  // Other Review-page orgs still offer "Proceed to Diagnostic Decision".
  const finishesSSIWithReport = wf.isMDTReview || wf.hasCIReview || asksAppointmentFinishPath;

  const [hasChanges, setHasChanges] = useState(false);

  // Route guard: only orgs configured for SSI Review & Synthesis should reach
  // this page. Standard-flow orgs are redirected to the Recommended Diagnosis page.
  useEffect(() => {
    if (!claims) return;
    if (!wf.showSSIReviewPage) {
      navigate(`/patient/${patientId}/proforma/semi-structured-interview/recommended-diagnosis`, { replace: true });
    }
  }, [claims, wf.showSSIReviewPage, patientId, navigate]);

  // DF data still lives under the CI path so the backend pipeline is unchanged.
  // Section 12 = Diagnostic Formulation in the CI data model.
  const DF_SECTION_NUMBER = 12;

  // --- Permissions ---
  // Before a Lead takeover: only the original clinicianSSI can edit Review
  // notes / differentials / finish the module.
  // After takeover: the live SSI belongs to the Lead. Emerging Formulation
  // and Confidential Notes are hidden here (they live in the snapshot of the
  // original clinician's proforma). The original SSI clinician can still
  // open this page, but only as a read-only viewer of the Lead's decisions.
  const overrideActive = isSSILeadOverrideActive(patientDataModel);
  const currentUid = auth.currentUser?.uid;
  const isCurrentUserLead = Boolean(
    currentUid && isLeadClinician(patientDataModel, currentUid)
  );
  const leadOverride =
    patientDataModel?.dataModel?.['3_semiStructuredInterview']?.leadOverride;
  const snapshotPath = leadOverride?.archiveId
    ? `/patient/${patientId}/ssi-snapshot/${leadOverride.archiveId}`
    : null;

  const [canEdit, setCanEdit] = useState(false);
  useEffect(() => {
    if (!currentUid || !patientDataModel) {
      setCanEdit(false);
      return;
    }
    // Freeze all Review edits once a takeover is active — including for the
    // original SSI clinician who previously owned these personal-note panels.
    if (overrideActive) {
      setCanEdit(false);
      return;
    }
    setCanEdit(isOriginalSSIClinician(patientDataModel, currentUid));
  }, [patientDataModel, currentUid, overrideActive]);

  // Personal-note panels (Emerging Formulation / Confidential Notes, and for
  // TOC the Emerging Differentials panel) are the original clinician's record.
  // Under an active Lead takeover they are not part of the live SSI view —
  // clinicians browse them on the read-only snapshot instead.
  const showPersonalNotesPanels = !overrideActive;

  // "Lead finisher" mode: the Lead viewing the live SSI they have taken over.
  // Only in this mode do we show the "Finish Semi-Structured Interview" CTA and
  // enforce a managed exit. The original SSI clinician (also read-only under
  // override) never sees the finish CTA — the takeover belongs to the Lead.
  const isLeadOverrideFinisher = overrideActive && isCurrentUserLead;

  // Guard-context hooks for the enforced-exit interceptor (mirrors 1_DD.js).
  // `forceDirty` (passed to useSectionExitRegeneration below) is what arms the
  // guard so breadcrumb / TopNav route through our interceptor.
  const { registerExitInterceptor, clearExitInterceptor, clearDirty } = useContext(
    ProformaNavigationGuardContext
  );

  // --- SSI Summary derivation (read-only) ---
  // Mirrors the exact derivation logic from 3_RDD.js so the clinician sees
  // the same symptom table and domains table that would appear on the RDD page.
  const SECTIONS_SSI = useMemo(() => {
    const ssiData = patientDataModel?.dataModel?.['3_semiStructuredInterview']?.sections;
    if (!ssiData) return [];
    return Object.entries(ssiData).map(([key, section]) => ({ key, ...section }));
  }, [patientDataModel]);

  // Raw symptom question data from the SSI Symptoms section
  const questionsSymptoms = useMemo(() => {
    const section = SECTIONS_SSI.find(s => s.sectionTitle === 'Symptoms');
    if (!section) return [];
    return Object.entries(section.questions || {}).map(([, question]) => question);
  }, [SECTIONS_SSI]);

  // Domain section data for the domains table
  const dataDomains = useMemo(() => {
    const section = SECTIONS_SSI.find(s => s.sectionTitle === 'Domains of Functional Impairment');
    if (!section) return null;
    return section;
  }, [SECTIONS_SSI]);

  // Structured per-symptom data split by DSM code prefix:
  // A1.x = Inattention, A2.x = Hyperactivity-Impulsivity
  const attentionDeficitData = questionsSymptoms
    .filter(item => item?.DSM_code && item.DSM_code.startsWith('A1'))
    .map(item => ({
      criterion: item.DSM_code,
      symptom: item.DSM_symptom,
      adulthood: item.symptomPresent_Adulthood,
      childhood: item.symptomPresent_Childhood,
    }));

  const hyperactivityImpulsivityData = questionsSymptoms
    .filter(item => item?.DSM_code && item.DSM_code.startsWith('A2'))
    .map(item => ({
      criterion: item.DSM_code,
      symptom: item.DSM_symptom,
      adulthood: item.symptomPresent_Adulthood,
      childhood: item.symptomPresent_Childhood,
    }));

  const calculateTotals = (data) => {
    const adulthoodTotal = data.filter(item => item.adulthood).length;
    const childhoodTotal = data.filter(item => item.childhood).length;
    return { adulthoodTotal, childhoodTotal };
  };

  const attentionTotals = calculateTotals(attentionDeficitData);
  const hyperactivityTotals = calculateTotals(hyperactivityImpulsivityData);

  // Structured domain-level impairment data for the summary table.
  // Each domain has a title and adulthood/childhood impairment flags.
  const domainImpairmentData = useMemo(() => {
    if (!dataDomains?.questions) return [];
    return Object.entries(dataDomains.questions)
      .sort(([a], [b]) => parseInt(a, 10) - parseInt(b, 10))
      .map(([, question]) => ({
        domain: question.DSM_symptom || question.DSM_symptom_prefix || 'Unknown',
        adulthood: question.functioningReduced_Adulthood,
        childhood: question.functioningReduced_Childhood,
      }));
  }, [dataDomains]);

  // --- SSI Reset banner data (3 Borough / SLaM workflow) ---
  const ssiResetInfo = useMemo(() => {
    const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
    if (!manageReports?.ssiResetRequired) return { isResetRequired: false };
    return {
      isResetRequired: true,
      resetRequestedByName: manageReports.ssiResetRequestedByName || 'Lead clinician',
      conflictType: manageReports.ssiResetConflictType || 'CI',
    };
  }, [patientDataModel]);

  // --- Differentials selection state (initialised from Firestore) ---
  // ADHD is always included as the "condition referred for" and cannot be
  // deselected.  If Firestore data doesn't already contain it (e.g. patient
  // was created before this change) we inject it on load.
  // SLaM (hasCIReview): reads from emergingFormulations.ssiClinician
  // TOC/legacy: reads from CI section 12
  const [differentials, setDifferentials] = useState(() => {
    if (wf.hasCIReview) {
      const ssiData = patientDataModel?.dataModel?.emergingFormulations?.ssiClinician;
      const saved = ssiData?.differentials || [];
      const withADHD = saved.includes(ADHD_CONDITION_LABEL) ? saved : [ADHD_CONDITION_LABEL, ...saved];
      return {
        selected: withADHD,
        isConfirmed: ssiData?.confirmed || false,
      };
    }
    const sectionData = patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.[DF_SECTION_NUMBER] || {};
    const saved = sectionData.differentialDiagnoses || [];
    const withADHD = saved.includes(ADHD_CONDITION_LABEL) ? saved : [ADHD_CONDITION_LABEL, ...saved];
    return {
      selected: withADHD,
      isConfirmed: saved.some(c => c !== ADHD_CONDITION_LABEL),
    };
  });

  // useSectionExitRegeneration: triggers CI 'Diagnostic Formulation' paragraph
  // regeneration on unmanaged exit (breadcrumb, browser back) when the clinician
  // has made changes and confirmed their differentials selection.
  // Skip for conditional-CAR workflows (TOC, SLaM, WLC — ci_DF not used in their
  // reports) and when hasCIReview is active (the Lead Clinician's CI Review page
  // owns the DF paragraph, not the SSI clinician).
  const { markRegenHandled } = useSectionExitRegeneration({
    patientId,
    stageTitle: 'Clinical Interview',
    sectionTitle: 'Diagnostic Formulation',
    hasChanges,
    canEdit,
    patientDataModel,
    shouldRegenerate: differentials.isConfirmed && !wf.usesConditionalCarPipeline && !wf.hasCIReview,
    // Arm the navigation guard when the Lead is in override-finisher mode so
    // breadcrumb / TopNav exits are intercepted and routed to the finish dialog.
    // This is the single source of dirty state (no separate registerDirty), and
    // regeneration on unmount still keys on hasChanges+canEdit, so a Lead who
    // made no note edits (canEdit=false) never triggers spurious regeneration.
    forceDirty: isLeadOverrideFinisher,
  });

  // --- Formulation Notes state ---
  // Lazy initializer reads once on mount; local state is never overwritten by
  // subsequent patientDataModel refreshes (avoids the race where a Firestore
  // write-refresh cycle clobbers text the user has typed but not yet blurred).
  // SLaM (hasCIReview): reads from emergingFormulations.ssiClinician.formulation
  // TOC/legacy: reads from CI section 12 clinicianNotes['Additional Notes']
  const [formulation, setFormulation] = useState(() => {
    if (wf.hasCIReview) {
      return patientDataModel?.dataModel?.emergingFormulations?.ssiClinician?.formulation || '';
    }
    return patientDataModel?.dataModel?.['2_clinicalInterview']
      ?.sections?.[DF_SECTION_NUMBER]?.clinicianNotes?.['Additional Notes'] || '';
  });

  // --- Internal Case Notes state ---
  // SLaM (hasCIReview): reads from emergingFormulations.ssiClinician.confidentialNotes
  // TOC/legacy: reads from SSI section 3 internalCaseNotes
  const [caseNotes, setCaseNotes] = useState(() => {
    if (wf.hasCIReview) {
      return patientDataModel?.dataModel?.emergingFormulations?.ssiClinician?.confidentialNotes || '';
    }
    return patientDataModel?.dataModel?.['3_semiStructuredInterview']
      ?.sections?.['3']?.internalCaseNotes || '';
  });

  // --- Firestore write helpers ---
  // Writes DF-related data to CI section 12 (differentials, formulation notes, status).
  // Used by TOC/legacy orgs where the SSI clinician writes directly to CI section 12.
  const sendDFUpdates = useCallback((newUpdates) => {
    if (!canEdit) return Promise.resolve();
    return updatePatientFirestore(patientId, {
      '2_clinicalInterview': {
        sections: { [DF_SECTION_NUMBER]: newUpdates }
      }
    });
  }, [patientId, updatePatientFirestore, canEdit]);

  // Writes SSI-scoped data to SSI section 3 (internal case notes)
  const sendSSIUpdates = useCallback((newUpdates) => {
    if (!canEdit) return Promise.resolve();
    return updatePatientFirestore(patientId, {
      '3_semiStructuredInterview': {
        sections: { '3': newUpdates }
      }
    });
  }, [patientId, updatePatientFirestore, canEdit]);

  // Writes emerging formulations to the dedicated dataModel.emergingFormulations field.
  // Used when hasCIReview is true: the SSI clinician writes their own emerging
  // differentials/formulation/notes independently from the Lead Clinician's CI section 12.
  // Attribution metadata is always included so DD pages can display who wrote what and when.
  const sendEmergingFormulationsUpdate = useCallback((newUpdates) => {
    if (!canEdit) return Promise.resolve();
    const user = auth.currentUser;
    return updatePatientFirestore(patientId, {
      emergingFormulations: {
        ssiClinician: {
          ...newUpdates,
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || null,
          updatedByName: user?.displayName || null,
        }
      }
    });
  }, [patientId, updatePatientFirestore, canEdit]);

  // --- Differentials handlers ---
  const handleSelect = (value) => {
    if (!canEdit) return;
    setHasChanges(true);
    setDifferentials(prev => {
      const current = prev.selected;
      const newValues = current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value];
      return { ...prev, selected: newValues };
    });
  };

  const handleConfirm = () => {
    if (!canEdit) return;
    setHasChanges(true);
    setDifferentials(prev => ({ ...prev, isConfirmed: true }));
    if (wf.hasCIReview) {
      sendEmergingFormulationsUpdate({ differentials: differentials.selected, confirmed: true });
    } else {
      sendDFUpdates({ differentialDiagnoses: differentials.selected });
    }
  };

  const handleChangeChoice = () => {
    if (!canEdit) return;
    setHasChanges(true);
    setDifferentials(prev => ({ ...prev, isConfirmed: false }));
  };

  // --- Notes handlers ---
  // onChange sets local state AND marks dirty (hasChanges). onBlur's SOLE job
  // is persisting to Firestore — it intentionally does NOT call setHasChanges.
  // This separation prevents false-positive dirty flags from DictationTextArea's
  // unmount safety-net blur (fires even when no text was edited).

  const handleFormulationChange = (value) => {
    if (!canEdit) return;
    setHasChanges(true);
    setFormulation(value);
  };

  const handleFormulationBlur = () => {
    if (!canEdit) return;

    if (wf.hasCIReview) {
      const storedValue = patientDataModel?.dataModel?.emergingFormulations?.ssiClinician?.formulation || '';
      if (formulation === storedValue) return;
      try {
        sendEmergingFormulationsUpdate({ formulation });
      } catch (error) {
        console.error('Error persisting formulation notes:', error);
      }
    } else {
      const storedValue = patientDataModel?.dataModel?.['2_clinicalInterview']
        ?.sections?.[DF_SECTION_NUMBER]?.clinicianNotes?.['Additional Notes'] || '';
      if (formulation === storedValue) return;
      try {
        sendDFUpdates({ clinicianNotes: { 'Additional Notes': formulation } });
      } catch (error) {
        console.error('Error persisting formulation notes:', error);
      }
    }
  };

  const handleCaseNotesChange = (value) => {
    if (!canEdit) return;
    setHasChanges(true);
    setCaseNotes(value);
  };

  const handleCaseNotesBlur = () => {
    if (!canEdit) return;

    if (wf.hasCIReview) {
      const storedValue = patientDataModel?.dataModel?.emergingFormulations?.ssiClinician?.confidentialNotes || '';
      if (caseNotes === storedValue) return;
      try {
        // Dual-write: canonical location + SSI section 3 for any existing reads
        sendEmergingFormulationsUpdate({ confidentialNotes: caseNotes });
        sendSSIUpdates({ internalCaseNotes: caseNotes });
      } catch (error) {
        console.error('Error persisting case notes:', error);
      }
    } else {
      const storedValue = patientDataModel?.dataModel?.['3_semiStructuredInterview']
        ?.sections?.['3']?.internalCaseNotes || '';
      if (caseNotes === storedValue) return;
      try {
        sendSSIUpdates({ internalCaseNotes: caseNotes });
      } catch (error) {
        console.error('Error persisting case notes:', error);
      }
    }
  };

  // --- Dialog and generation state ---
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  // Appointment finish wizard (one-clinician SLaM): true while Path 1 / Path 2
  // Firestore writes are in flight so the wizard buttons cannot double-submit.
  const [isFinishPathSubmitting, setIsFinishPathSubmitting] = useState(false);
  const clinicianId = auth.currentUser?.uid;

  // --- Lead-override finish flow state ---
  // A dedicated dialog/handler for the Lead finishing the SSI they took over.
  // Kept fully separate from the original-clinician completion flow above
  // (`isDialogOpen` / `handleFinishSSI`) to avoid any regression to that path —
  // the two are never active in the same mode (`canEdit` is false under override,
  // and the finish CTA is Lead-gated). `isFinishingRef` lets the enforced-exit
  // interceptor become a pass-through once the Lead confirms, so their own
  // navigation (to Documents or DD) is not re-trapped by the finish dialog.
  const [leadFinishDialogOpen, setLeadFinishDialogOpen] = useState(false);
  const isFinishingRef = useRef(false);
  const exitInterceptorRef = useRef(null);

  // --- Central managed-exit function ---
  // Every navigation path out of this page funnels through here to ensure
  // consistent flush, persist, and regeneration triggering.
  const isGenerated = !!(patientDataModel?.dataModel?.['6_manageReports']?.sections?.['1']?.stageSectionsGenerated?.includes?.('Diagnostic Formulation'));

  const handleManagedNavigation = useCallback((route) => {
    if (!canEdit) {
      navigate(route);
      return;
    }

    markRegenHandled();

    // Persist latest state to the appropriate data location
    if (wf.hasCIReview) {
      sendEmergingFormulationsUpdate({
        differentials: differentials.selected,
        formulation,
        confidentialNotes: caseNotes,
        confirmed: differentials.isConfirmed,
      });
      sendSSIUpdates({ internalCaseNotes: caseNotes });
    } else {
      // When emerging differentials are optional, only persist selections the
      // clinician explicitly confirmed. Without this guard an unconfirmed
      // checkbox state could leak into Firestore and create an unintended
      // Other Conditions step in DD.
      const confirmedDifferentials = differentials.isConfirmed
        ? differentials.selected
        : (wf.requireEmergingDifferentials ? differentials.selected : []);

      sendDFUpdates({
        differentialDiagnoses: confirmedDifferentials,
        clinicianNotes: { 'Additional Notes': formulation },
      });
      sendSSIUpdates({ internalCaseNotes: caseNotes });
    }

    const dataReady = flushPendingUpdates();

    // Trigger DF paragraph generation if changes were made and differentials confirmed.
    // Skip for conditional-CAR workflows (TOC, SLaM, WLC — ci_DF not in their reports)
    // and when hasCIReview is active (Lead Clinician owns the DF paragraph).
    if (hasChanges && differentials.isConfirmed && !isGenerated && !wf.usesConditionalCarPipeline && !wf.hasCIReview) {
      const conditions = [];
      if (isPatientFemale(patientDataModel)) {
        conditions.push('patientIsFemale');
      }
      TriggerGenerateDocumentSection(patientId, 'Clinical Interview', 'Diagnostic Formulation', conditions, dataReady);
    }

    navigate(route);
  }, [canEdit, navigate, markRegenHandled, flushPendingUpdates, sendDFUpdates, sendSSIUpdates,
      sendEmergingFormulationsUpdate, differentials.selected, differentials.isConfirmed,
      formulation, caseNotes, hasChanges, isGenerated, patientId, patientDataModel,
      wf.usesConditionalCarPipeline, wf.hasCIReview, wf.requireEmergingDifferentials]);

  // --- Completion flow ---
  // Persists the clinician's emerging data and opens the completion dialog.
  // For SLaM (hasCIReview): writes to emergingFormulations.ssiClinician only —
  // CI section 12 status is NOT marked complete (the Lead Clinician owns that).
  // For TOC/legacy: writes to CI section 12 with status 'completed'.
  const handleComplete = useCallback(async () => {
    if (!canEdit) return;
    setIsSaving(true);

    if (wf.hasCIReview) {
      sendEmergingFormulationsUpdate({
        differentials: differentials.selected,
        formulation,
        confidentialNotes: caseNotes,
        confirmed: true,
      });
      sendSSIUpdates({ internalCaseNotes: caseNotes });
    } else {
      // Same unconfirmed-selection guard as handleManagedNavigation: when
      // emerging differentials are optional, only write confirmed selections
      // so an accidental checkbox does not create an Other Conditions step.
      const confirmedDifferentials = differentials.isConfirmed
        ? differentials.selected
        : (wf.requireEmergingDifferentials ? differentials.selected : []);

      sendDFUpdates({
        differentialDiagnoses: confirmedDifferentials,
        clinicianNotes: { 'Additional Notes': formulation },
        status: 'completed',
        progress: 100,
      });
      sendSSIUpdates({ internalCaseNotes: caseNotes });
    }

    await flushPendingUpdates();

    setIsSaving(false);

    // Re-entry guard (one-clinician SLaM): if the appointment finish path was
    // already chosen on an earlier visit, do not re-run the wizard — flipping
    // Path 1 <-> Path 2 after the SSI is completed would desynchronise the
    // overview order and the provisional PAD outcome. Mirrors the CI guard in
    // 12_DF.js handleNextSection.
    //   padInAppointment -> straight back into the PAD review.
    //   deferPad         -> the SSI report already exists; open its review page.
    if (asksAppointmentFinishPath && wf.ciFinishPath) {
      if (wf.ciFinishPath === CI_FINISH_PATHS.PAD_IN_APPOINTMENT) {
        navigate(`/patient/${patientId}/proforma/post-assessment-discussion/review-assessment`);
        return;
      }
      if (wf.ciFinishPath === CI_FINISH_PATHS.DEFER_PAD
          && patientDataModel?.dataModel?.['6_manageReports']?.ssiReportGenerated) {
        navigate(`/patient/${patientId}/review-document-ssi`);
        return;
      }
    }

    setIsDialogOpen(true);
  }, [canEdit, flushPendingUpdates, sendDFUpdates, sendSSIUpdates,
      sendEmergingFormulationsUpdate, differentials.selected, differentials.isConfirmed,
      formulation, caseNotes, wf.hasCIReview, wf.requireEmergingDifferentials,
      asksAppointmentFinishPath, wf.ciFinishPath, patientDataModel, patientId, navigate]);

  // --- Post-dialog navigation ---
  // Sets SSI module complete, triggers report generation, navigates out.
  const handleFinishSSI = useCallback(() => {
    setIsDialogOpen(false);

    const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
    const firstCompletedFields = buildFirstCompletedFields(patientDataModel?.dataModel?.['3_semiStructuredInterview']);

    const updateData = {
      '3_semiStructuredInterview': {
        status: 'completed',
        progress: 100,
        docs_available: true,
        lastCompletedAt: serverTimestamp(),
        lastCompletedBy: auth.currentUser.uid,
        lastCompletedByName: auth.currentUser.displayName,
        ...firstCompletedFields,
      },
      '6_manageReports': {
        ssiReportGenerated: true,
        ssiReportGeneratedAt: serverTimestamp(),
        ssiReportGeneratedBy: auth.currentUser.uid,
        ssiReportGeneratedByName: auth.currentUser.displayName,
        ...buildFirstGeneratedFields('ssi', manageReports),
      }
    };

    // Clear SSI reset flag if applicable (3 Borough / SLaM workflow)
    if (manageReports?.ssiResetRequired && wf.isSSIFirst) {
      updateData['6_manageReports'].ssiResetRequired = false;
      updateData['6_manageReports'].ssiResetCompletedAt = serverTimestamp();
      updateData['6_manageReports'].ssiResetCompletedBy = auth.currentUser.uid;
      updateData['6_manageReports'].ssiResetCompletedByName = auth.currentUser.displayName;
    }

    updatePatientFirestore(patientId, updateData);

    appendReportAuditEntry(patientId, {
      module: 'ssi',
      eventType: 'generated',
      isFirstOccurrence: !manageReports?.ssiReportFirstGeneratedAt,
    });

    // Inferred appointment audit on first completion (fire-and-forget; does not write appointmentDates)
    recordInferredAppointmentDateOnCompletion({
      patientId,
      moduleKey: 'ssi',
      didWriteFirstCompleted: Object.keys(firstCompletedFields).length > 0,
      enabled: wf.isAppointmentDatesEnabled,
    });

    // Trigger DF paragraph generation chained to the flush.
    // Skip for conditional-CAR workflows (TOC, SLaM, WLC — ci_DF not in their
    // reports) and when hasCIReview is active (Lead owns the DF paragraph).
    const dataReady = flushPendingUpdates();
    if (!wf.usesConditionalCarPipeline && !wf.hasCIReview) {
      const conditions = isPatientFemale(patientDataModel) ? ['patientIsFemale'] : [];
      TriggerGenerateDocumentSection(patientId, 'Clinical Interview', 'Diagnostic Formulation', conditions, dataReady);
    }

    if (finishesSSIWithReport || wf.hasMultiStepDD) {
      // MDT / SLaM / multi-step DD: simulated progress dialog, then Documents.
      // The SSI clinician's job ends at report review — DD is for the Lead.
      setIsGenerating(true);
    } else {
      // Standard DD: navigate directly to Diagnostic Decision
      navigate(`/patient/${patientId}/proforma/diagnostic-decision/primary-diagnosis`);
    }
  }, [patientId, patientDataModel, updatePatientFirestore, navigate,
      flushPendingUpdates, finishesSSIWithReport, wf.isSSIFirst, wf.isTOCWorkflow,
      wf.hasCIReview, wf.hasMultiStepDD, wf.isAppointmentDatesEnabled]);

  // --- Appointment finish wizard: Path 2 (one-clinician SLaM) ---
  // "End appointment here": record deferPad on the patient root (overview
  // flips to DD ahead of PAD), then run the normal SSI finish (module
  // completion + report generated + audit) and the progress dialog → Documents.
  // workflowDecisions lives outside dataModel, so the decision cannot go
  // through updatePatientFirestore and is written in its own transaction.
  const handleFinishSSIDeferPad = useCallback(async () => {
    if (!canEdit) return;
    setIsFinishPathSubmitting(true);
    try {
      await runTransaction(db, async (transaction) => {
        transaction.update(doc(db, 'patients', patientId), buildDeferPadUpdates());
      });
      handleFinishSSI();
    } catch (error) {
      console.error('Failed to set appointment finish path (deferPad):', error);
      alert('Could not save your choice. Please try again.');
    } finally {
      setIsFinishPathSubmitting(false);
    }
  }, [canEdit, patientId, handleFinishSSI]);

  // --- Appointment finish wizard: Path 1 (one-clinician SLaM) ---
  // "Share outcome now": one transaction writes the decision, the provisional
  // ADHD outcome for the PAD, SSI completion + ssiReport* generated fields,
  // and unlocks DD/PAD if still locked (shared builder — identical shape to the
  // CI entry point in 12_DF.js). Then straight into the PAD review. SSI
  // paragraphs were already kicked off section by section as the clinician
  // left Symptoms / Domains, so nothing extra needs generating here.
  const handleConfirmSsiPadPath = useCallback(async ({ provisionalAdhdOutcome }) => {
    if (!canEdit) return;
    setIsFinishPathSubmitting(true);
    try {
      const { updates, didWriteFirstCompleted, isFirstGeneration } =
        buildPadInAppointmentUpdates({
          module: 'ssi',
          patientDataModel,
          user: auth.currentUser,
          provisionalAdhdOutcome,
        });

      await runTransaction(db, async (transaction) => {
        transaction.update(doc(db, 'patients', patientId), updates);
      });

      appendReportAuditEntry(patientId, {
        module: 'ssi',
        eventType: 'generated',
        isFirstOccurrence: isFirstGeneration,
      });

      // Inferred appointment audit on first SSI completion (fire-and-forget;
      // does not write appointmentDates) — same as handleFinishSSI.
      recordInferredAppointmentDateOnCompletion({
        patientId,
        moduleKey: 'ssi',
        didWriteFirstCompleted,
        enabled: wf.isAppointmentDatesEnabled,
      });

      // We navigate directly rather than via handleManagedNavigation (the
      // emerging data was already persisted by handleComplete), so tell the
      // exit-regeneration hook this exit is handled.
      markRegenHandled();
      setIsDialogOpen(false);
      navigate(`/patient/${patientId}/proforma/post-assessment-discussion/review-assessment`);
    } catch (error) {
      console.error('Failed to start PAD-in-appointment path:', error);
      alert('Could not start the Post-Assessment Discussion. Please try again.');
    } finally {
      setIsFinishPathSubmitting(false);
    }
  }, [canEdit, patientId, patientDataModel, navigate, markRegenHandled, wf.isAppointmentDatesEnabled]);

  const handleCancelGeneration = useCallback(() => {
    setIsGenerating(false);
  }, []);

  // Called by GenerateReportProgressDialog when simulated progress completes
  const handleAfterGeneration = useCallback(() => {
    handleManagedNavigation(`/patient/${patientId}/documents`);
  }, [patientId, handleManagedNavigation]);

  // --- Lead-override finish handler ---
  // Runs when the Lead confirms "Finish Semi-Structured Interview" on the SSI
  // they have taken over. Two destinations:
  //   'documents' → the DD is already complete, so we go straight to the
  //     Documents Dashboard (via the simulated progress dialog) where the SSI
  //     report section will be waiting in the purple "ready for review" state
  //     (the Lead's Symptoms/Domains edits already regenerated those paragraphs).
  //   'dd'        → the DD is not yet complete, so we send the Lead back to the
  //     Diagnostic Decision to reaffirm their decision against the fresh counts.
  //
  // The Firestore write is minimal and (mostly) idempotent: the SSI module is
  // already `completed` (a takeover can only start once the SSI is finished), so
  // we simply re-affirm completion and mark the report as (re)generated. We
  // deliberately do NOT touch `emergingFormulations.ssiClinician` — that is the
  // original clinician's personal record, hidden under override — and we do not
  // trigger CI Diagnostic-Formulation regen (SLaM's Lead owns the DF paragraph,
  // not the SSI clinician). `firstCompletedAt` / `firstGeneratedAt` are
  // write-once and preserved via the buildFirst* helpers.
  const handleLeadFinishSSI = useCallback((destination) => {
    // Mark the CTA in progress so the exit interceptor lets our own navigation
    // through instead of re-opening the finish dialog. Best-effort clear of the
    // guard state (the destination page unmounts this one and its
    // useSectionExitRegeneration cleanup clears dirty regardless).
    isFinishingRef.current = true;
    clearDirty();
    clearExitInterceptor();
    setLeadFinishDialogOpen(false);

    const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
    updatePatientFirestore(patientId, {
      '3_semiStructuredInterview': {
        status: 'completed',
        progress: 100,
        docs_available: true,
        lastCompletedAt: serverTimestamp(),
        lastCompletedBy: auth.currentUser.uid,
        lastCompletedByName: auth.currentUser.displayName,
        // No-op if firstCompletedAt already set (write-once).
        ...buildFirstCompletedFields(patientDataModel?.dataModel?.['3_semiStructuredInterview']),
      },
      '6_manageReports': {
        ssiReportGenerated: true,
        ssiReportGeneratedAt: serverTimestamp(),
        ssiReportGeneratedBy: auth.currentUser.uid,
        ssiReportGeneratedByName: auth.currentUser.displayName,
        // No-op if firstGeneratedAt already set (write-once).
        ...buildFirstGeneratedFields('ssi', manageReports),
      },
    });

    appendReportAuditEntry(patientId, {
      module: 'ssi',
      eventType: 'generated',
      isFirstOccurrence: !manageReports?.ssiReportFirstGeneratedAt,
    });

    // Audit trail for the takeover: record a distinct recompletion event and a
    // compact summary of which symptom / domain decisions the Lead changed
    // relative to the original clinician's archived snapshot. Fire-and-forget
    // and fully decoupled — it never blocks navigation and never throws.
    recordLeadOverrideRecompletion({
      patientId,
      archiveId: leadOverride?.archiveId,
      liveSSISections: patientDataModel?.dataModel?.['3_semiStructuredInterview']?.sections,
    });

    flushPendingUpdates();

    if (destination === 'documents') {
      // Reuse the simulated progress dialog; handleAfterGeneration → /documents.
      setIsGenerating(true);
    } else {
      // Resume the Diagnostic Decision. For an entered SLaM DD (the only
      // reachable state, since a takeover is initiated from the DD page) this
      // resolves to .../diagnostic-decision/adhd.
      navigate(
        getDiagnosticDecisionStartRoute({
          patientId,
          patientDataModel,
          isFlatDifferentials: wf.isFlatDifferentials,
          hasMultiStepDD: wf.hasMultiStepDD,
          isTOCWorkflow: wf.isTOCWorkflow,
        })
      );
    }
  }, [patientId, patientDataModel, leadOverride, updatePatientFirestore, flushPendingUpdates,
      navigate, clearDirty, clearExitInterceptor, wf.isFlatDifferentials,
      wf.hasMultiStepDD, wf.isTOCWorkflow]);

  // --- Enforced managed exit for the Lead-override finisher ---
  // Mirrors the 1_DD.js exit-guard pattern. When the Lead has taken over the
  // SSI, `forceDirty` (passed to useSectionExitRegeneration) arms the navigation
  // guard, so breadcrumb / TopNav / DD-sidenav exits are routed through this
  // interceptor. We enforce a managed exit by re-opening the finish dialog
  // (discarding the destination) unless:
  //   - a logout callback is supplied (safety valve — never trap sign-out), or
  //   - the Lead's own finish CTA is already in progress (isFinishingRef).
  // "Back to Domains" uses handleManagedNavigation (a direct navigate with
  // canEdit=false), so intra-SSI editing is never trapped.
  useEffect(() => {
    if (!isLeadOverrideFinisher) return;

    exitInterceptorRef.current = (destinationUrl, onConfirmCallback) => {
      // Logout / callback-driven exits must never be trapped behind the dialog.
      if (onConfirmCallback) {
        onConfirmCallback();
        return;
      }
      // The Lead's own confirmed finish navigation — let it through.
      if (isFinishingRef.current) {
        navigate(destinationUrl);
        return;
      }
      // Enforce a managed exit: prompt the finish choice, discard destination.
      setLeadFinishDialogOpen(true);
    };

    registerExitInterceptor(exitInterceptorRef.current);
    return () => clearExitInterceptor();
  }, [isLeadOverrideFinisher, registerExitInterceptor, clearExitInterceptor, navigate]);

  // --- Navigation ---
  const handleBack = useCallback(() => {
    handleManagedNavigation(`/patient/${patientId}/proforma/semi-structured-interview/domains`);
  }, [patientId, handleManagedNavigation]);

  // --- Breadcrumb ---
  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}`],
    ['Semi-Structured Interview'],
    ['Review & Synthesis']
  ];

  // Filter ADHD out of the selectable conditions: ADHD is the "referred
  // condition" for an ADHD assessment so it is displayed as a separate
  // read-only panel rather than being mixed in with the emerging
  // differentials the clinician selects.
  const filteredConditions = useMemo(() => {
    const result = {};
    for (const [category, conditions] of Object.entries(AccordionData)) {
      const filtered = conditions.filter(c => c.icd_11_label !== ADHD_CONDITION_LABEL);
      if (filtered.length > 0) result[category] = filtered;
    }
    return result;
  }, []);

  // Forward button gating:
  //   SLaM (hasCIReview): panel hidden, always ready.
  //   WLC V2 (!requireEmergingDifferentials): panel visible but optional —
  //     clinician can Complete without selecting or confirming differentials.
  //   TOC (requireEmergingDifferentials): must confirm at least one non-ADHD
  //     differential before Complete is enabled.
  const isFormReady = wf.hasCIReview || !wf.requireEmergingDifferentials
    || (differentials.isConfirmed
       && differentials.selected.filter(c => c !== ADHD_CONDITION_LABEL).length > 0);

  // --- Differentials recap for the completion dialog ---
  // ADHD is always injected into differentials.selected (it is the referred
  // condition), so we filter it out here to avoid showing it under both
  // "Condition referred for" and "Emerging differentials" in the dialog.
  const constructDifferentialsRecap = useCallback(() => {
    const filtered = differentials.selected.filter(c => c !== ADHD_CONDITION_LABEL);
    if (filtered.length === 0) {
      return <p className="text-sm italic text-gray-400">No conditions selected</p>;
    }
    return filtered.map((condition, i) => (
      <div key={i} className="mb-1">• {condition}</div>
    ));
  }, [differentials.selected]);

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />

      {/* SSI Reset Banner - shown when Lead clinician is re-editing SSI */}
      <div className="px-8">
        <SSIResetBanner
          isVisible={ssiResetInfo.isResetRequired}
          resetRequestedByName={ssiResetInfo.resetRequestedByName}
          conflictType={ssiResetInfo.conflictType}
        />
        {/* Lead takeover banner — copy differs for Lead vs original SSI clinician */}
        {overrideActive && (
          <div className="mt-3">
            <SSIOverrideBanner
              isVisible
              originalClinicianName={
                leadOverride?.originalClinicianName ||
                patientDataModel?.clinicians?.clinicianSSI?.clinicianName ||
                'the original SSI clinician'
              }
              snapshotPath={snapshotPath}
              isCurrentUserLead={isCurrentUserLead}
            />
          </div>
        )}
      </div>

      <div className="flex-grow p-8">
       <div className="w-[70%] mx-auto">
        {/* Top navigation bar */}
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={handleBack}
            disabled={isSaving}
            className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 ${isSaving ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50'}`}
          >
            {isSaving
              ? <ButtonSpinner className="w-5 h-5 mr-2" />
              : <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />}
            <span className="font-bold">Back to Domains</span>
          </button>
          {canEdit && (
            <button
              onClick={handleComplete}
              className={`flex items-center py-2 px-4 rounded-md shadow-md transition-all duration-300 ${
                isFormReady
                  ? 'hover:bg-violet-800 bg-violet-900 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              disabled={!isFormReady || isSaving}
            >
              <span className="font-bold">
                {isSaving
                  ? 'Saving Progress...'
                  : finishesSSIWithReport
                    ? 'Finish Semi-Structured Interview'
                    : 'Proceed to Diagnostic Decision'}
              </span>
              {isSaving
                ? <ButtonSpinner className="w-5 h-5 ml-2" />
                : <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
            </button>
          )}
          {/* Lead-override finisher: the Lead who has taken over the SSI ends it
              here. Mutually exclusive with the canEdit button above (canEdit is
              false under override). No isFormReady gate — SLaM finishes on the
              decision summary alone. */}
          {isLeadOverrideFinisher && (
            <button
              onClick={() => setLeadFinishDialogOpen(true)}
              className="flex items-center py-2 px-4 rounded-md shadow-md transition-all duration-300 hover:bg-violet-800 bg-violet-900 text-white"
            >
              <span className="font-bold">Finish Semi-Structured Interview</span>
              <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
            </button>
          )}
        </div>

        <h2 className="text-2xl font-bold mb-6">Review & Synthesis</h2>

        {/* SSI Summary Panel — compact overview with totals and dialog
            triggers for the full symptom/domain tables. Keeps decision panels
            (ED, EF, CCN) above the fold while still providing full data access. */}
        <div className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mb-6">
          <h3 className="font-bold text-lg mb-4">Summary of Semi-Structured Interview Decisions</h3>
          <div className="flex gap-3">
            {/* View Symptoms dialog */}
            <Dialog.Root>
              <Dialog.Trigger asChild>
                <button className="px-4 py-2 bg-violet-900 hover:bg-violet-800 text-white text-sm font-semibold rounded-md transition-colors flex items-center gap-1">
                  <SearchIcon className="h-4 w-4" />
                  View Symptoms Decisions
                </button> 
              </Dialog.Trigger>
              <Dialog.Portal>
                <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000]" />
                <Dialog.Content className="radix-dialog-content-centered-motion fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-5xl max-h-[80vh] overflow-y-auto bg-white rounded-lg shadow-lg p-6 focus:outline-none z-[1001]">
                  <div className="flex justify-between items-center mb-4">
                    <Dialog.Title className="text-xl font-semibold">Symptoms Summary</Dialog.Title>
                    <Dialog.Close asChild>
                      <button className="rounded-full p-1 hover:bg-gray-200 focus:outline-none" aria-label="Close">
                        <XIcon className="h-5 w-5" />
                      </button>
                    </Dialog.Close>
                  </div>
                  <Dialog.Description className="sr-only">
                    DSM-5 symptom criteria present during adulthood and childhood
                  </Dialog.Description>
                  <table className="w-full text-left shadow-lg rounded-lg overflow-hidden" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '50%' }} />
                      <col style={{ width: '20%' }} />
                      <col style={{ width: '20%' }} />
                    </colgroup>
                    <thead>
                      <tr className="bg-primary text-white">
                        <th className="p-3 text-center">Criterion DSM-5</th>
                        <th className="p-3">Symptom</th>
                        <th className="p-3 text-center">Present during adulthood</th>
                        <th className="p-3 text-center">Present during childhood</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attentionDeficitData.map((item, index) => (
                        <tr key={index} className="bg-primary/5 border-b border-primary/10">
                          <td className="p-3 text-sm text-center font-bold text-primary">{item.criterion}</td>
                          <td className="p-3 text-xs">{item.symptom}</td>
                          <td className="p-3 text-center bg-white">
                            {item.adulthood === true
                              ? <CheckCircleIcon className="w-5 h-5 text-green-500 mx-auto" />
                              : item.adulthood === false
                                ? <XCircleIcon className="w-5 h-5 text-red-500 mx-auto" />
                                : <span className="text-gray-400 text-xs">{'\u2014'}</span>}
                          </td>
                          <td className="p-3 text-center bg-white">
                            {item.childhood === true
                              ? <CheckCircleIcon className="w-5 h-5 text-green-500 mx-auto" />
                              : item.childhood === false
                                ? <XCircleIcon className="w-5 h-5 text-red-500 mx-auto" />
                                : <span className="text-gray-400 text-xs">{'\u2014'}</span>}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-primary/15 font-bold text-primary">
                        <td className="p-3" colSpan="2">Total criteria: Attention Deficit</td>
                        <td className="p-3 text-center">{attentionTotals.adulthoodTotal} / 9</td>
                        <td className="p-3 text-center">{attentionTotals.childhoodTotal} / 9</td>
                      </tr>
                      {hyperactivityImpulsivityData.map((item, index) => (
                        <tr key={index} className="bg-primary/5 border-b border-primary/10">
                          <td className="p-3 text-sm text-center font-bold text-primary">{item.criterion}</td>
                          <td className="p-3 text-xs">{item.symptom}</td>
                          <td className="p-3 text-center bg-white">
                            {item.adulthood === true
                              ? <CheckCircleIcon className="w-5 h-5 text-green-500 mx-auto" />
                              : item.adulthood === false
                                ? <XCircleIcon className="w-5 h-5 text-red-500 mx-auto" />
                                : <span className="text-gray-400 text-xs">{'\u2014'}</span>}
                          </td>
                          <td className="p-3 text-center bg-white">
                            {item.childhood === true
                              ? <CheckCircleIcon className="w-5 h-5 text-green-500 mx-auto" />
                              : item.childhood === false
                                ? <XCircleIcon className="w-5 h-5 text-red-500 mx-auto" />
                                : <span className="text-gray-400 text-xs">{'\u2014'}</span>}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-primary/15 font-bold text-primary">
                        <td className="p-3" colSpan="2">Total Hyperactivity / Impulsivity</td>
                        <td className="p-3 text-center">{hyperactivityTotals.adulthoodTotal} / 9</td>
                        <td className="p-3 text-center">{hyperactivityTotals.childhoodTotal} / 9</td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="mt-4 flex justify-end">
                    <Dialog.Close asChild>
                      <button className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition-colors font-semibold">Close</button>
                    </Dialog.Close>
                  </div>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>

            {/* View Domains dialog — only shown when domain data exists */}
            {domainImpairmentData.length > 0 && (
              <Dialog.Root>
                <Dialog.Trigger asChild>
                  <button className="px-4 py-2 bg-violet-900 hover:bg-violet-800 text-white text-sm font-semibold rounded-md transition-colors flex items-center gap-1">
                    <SearchIcon className="h-4 w-4" />
                    View Impairment Decisions
                  </button>
                </Dialog.Trigger>
                <Dialog.Portal>
                  <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000]" />
                  <Dialog.Content className="radix-dialog-content-centered-motion fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-5xl max-h-[80vh] overflow-y-auto bg-white rounded-lg shadow-lg p-6 focus:outline-none z-[1001]">
                    <div className="flex justify-between items-center mb-4">
                      <Dialog.Title className="text-xl font-semibold">Domains of Functional Impairment</Dialog.Title>
                      <Dialog.Close asChild>
                        <button className="rounded-full p-1 hover:bg-gray-200 focus:outline-none" aria-label="Close">
                          <XIcon className="h-5 w-5" />
                        </button>
                      </Dialog.Close>
                    </div>
                    <Dialog.Description className="sr-only">
                      Functional impairment domains during adulthood and childhood
                    </Dialog.Description>
                    <table className="w-full text-left shadow-lg rounded-lg overflow-hidden" style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: '60%' }} />
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '20%' }} />
                      </colgroup>
                      <thead>
                        <tr className="bg-primary text-white">
                          <th className="p-3">Domain of Functional Impairment</th>
                          <th className="p-3 text-center">Impaired during adulthood</th>
                          <th className="p-3 text-center">Impaired during childhood</th>
                        </tr>
                      </thead>
                      <tbody>
                        {domainImpairmentData.map((item, index) => (
                          <tr key={index} className="bg-primary/5 border-b border-primary/10">
                            <td className="p-3 text-sm font-semibold text-primary">{item.domain}</td>
                            <td className="p-3 text-center bg-white">
                              {item.adulthood === true
                                ? <CheckCircleIcon className="w-5 h-5 text-green-500 mx-auto" />
                                : item.adulthood === false
                                  ? <XCircleIcon className="w-5 h-5 text-red-500 mx-auto" />
                                  : <span className="text-gray-400 text-xs">{'\u2014'}</span>}
                            </td>
                            <td className="p-3 text-center bg-white">
                              {item.childhood === true
                                ? <CheckCircleIcon className="w-5 h-5 text-green-500 mx-auto" />
                                : item.childhood === false
                                  ? <XCircleIcon className="w-5 h-5 text-red-500 mx-auto" />
                                  : <span className="text-gray-400 text-xs">{'\u2014'}</span>}
                            </td>
                          </tr>
                        ))}
                        <tr className="bg-primary/15 font-bold text-primary">
                          <td className="p-3">Total domains impaired</td>
                          <td className="p-3 text-center">{domainImpairmentData.filter(d => d.adulthood).length} / {domainImpairmentData.length}</td>
                          <td className="p-3 text-center">{domainImpairmentData.filter(d => d.childhood).length} / {domainImpairmentData.length}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="mt-4 flex justify-end">
                      <Dialog.Close asChild>
                        <button className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition-colors font-semibold">Close</button>
                      </Dialog.Close>
                    </div>
                  </Dialog.Content>
                </Dialog.Portal>
              </Dialog.Root>
            )}
          </div>
        </div>

        {/* Condition(s) Referred For — read-only panel showing ADHD as the
            primary reason for this assessment. Visually separated from the
            clinician-selectable Emerging Differentials below.
            Hidden for SLaM (hasCIReview): their SSI Review only needs the
            symptom/domain panel and confidential notes. */}
        {!wf.hasCIReview && (
          <div className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mt-6">
            <h3 className="font-bold text-lg mb-2">Condition(s) Referred For</h3>
            <div className="mt-3 p-3 bg-gray-100 border border-gray-200 rounded-lg">
              <p className="text-base text-gray-700 flex items-start gap-2">
                <span className="text-gray-400 mt-0.5">•</span>
                <span className="font-semibold">{ADHD_CONDITION_LABEL}</span>
              </p>
            </div>
          </div>
        )}

        {/* Personal-note panels are hidden once a Lead takeover is active —
            the original clinician's Emerging Formulation / Confidential Notes
            (and TOC Emerging Differentials) are browsed on the snapshot instead.
            The live page under override is the Lead's decisions-summary view. */}
        {showPersonalNotesPanels && (
          <>
            {/* Emerging Differentials — multi-select conditions panel (ADHD excluded).
                Hidden for SLaM (hasCIReview): only the Lead Clinician records emerging
                differentials during their CI Review & Synthesis. */}
            {/* WLC differentials flow into the DD's Other Conditions step
                and ARE included in the final report → gray/default panel.
                TOC skips per-differential decisions so they are
                informational only → amber + "Not included" footer. Same
                conditional logic the DD Review page already applies
                (skipDifferentialsDecisions). */}
            {!wf.hasCIReview && (
              <div className="flex flex-col gap-6 mt-6">
                <ConditionSelectionPanelTOC
                  title="Emerging Differentials"
                  variant={wf.skipDifferentialsDecisions ? 'non-report' : 'default'}
                  searchable
                  showSelectedBullets
                  subtitle="Conditions you're actively considering as alternative explanations, co-occurring conditions, or to rule in/out when you finalise your Diagnostic Decision."
                  selectedOptions={differentials.selected.filter(c => c !== ADHD_CONDITION_LABEL)}
                  isMultiSelect={true}
                  isConfirmed={differentials.isConfirmed}
                  onSelect={handleSelect}
                  onConfirm={handleConfirm}
                  onChangeChoice={handleChangeChoice}
                  conditions={filteredConditions}
                  allowEmptyConfirm={!wf.requireEmergingDifferentials}
                  canEdit={canEdit}
                />
              </div>
            )}

            {/* Emerging Formulation — free-text draft for MDT/DD reference (not in
                the patient report). Same amber "Not included in final report"
                styling for TOC and SLaM; SLaM persists to emergingFormulations.ssiClinician. */}
            <div className="bg-amber-50 rounded-2xl border border-amber-200 text-left px-6 pt-6 pb-2 shadow-md mt-6 flex flex-col">
              <h3 className="font-bold text-lg mb-2">Emerging Formulation</h3>
              <p className="text-sm text-gray-600 mb-4">
                Your working hypothesis about what's going on for this person and why. Capture the presenting picture, contributing factors, functional impact and strengths. Finalised at Diagnostic Decision.
              </p>
              <DictationTextArea
                value={formulation}
                onChange={handleFormulationChange}
                onBlur={handleFormulationBlur}
                canEdit={canEdit}
                ariaLabel="Emerging formulation notes"
              />
              <p className="text-xs italic text-amber-900/80 text-right mt-1 mb-0">
                Not included in final report
              </p>
            </div>

            {/* Confidential Notes — private clinician notes for their own reference (not in patient report) */}
            <div className="bg-amber-50 rounded-2xl border border-amber-200 text-left px-6 pt-6 pb-2 shadow-md mt-6 flex flex-col">
              <h3 className="font-bold text-lg mb-2">Confidential Notes</h3>
              <p className="text-sm text-gray-600 mb-4">
                {wf.hasCIReview
                  ? 'Confidential or sensitive observations for your own reference (not included in the patient report).'
                  : 'Confidential or sensitive observations for review at Diagnostic Decision.'}
              </p>
              <DictationTextArea
                value={caseNotes}
                onChange={handleCaseNotesChange}
                onBlur={handleCaseNotesBlur}
                canEdit={canEdit}
                ariaLabel="Confidential case notes"
              />
              <p className="text-xs italic text-amber-900/80 text-right mt-1 mb-0">
                Not included in final report
              </p>
            </div>
          </>
        )}

        {/* Bottom navigation */}
        <div className="flex justify-end items-center mt-4 pt-4 border-t border-gray-200">
          {canEdit && (
            <button
              onClick={handleComplete}
              className={`flex items-center py-2 px-4 rounded-md shadow-md transition-all duration-300 ${
                isFormReady
                  ? 'hover:bg-violet-800 bg-violet-900 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              disabled={!isFormReady || isSaving}
            >
              <span className="font-bold">
                {isSaving
                  ? 'Saving Progress...'
                  : finishesSSIWithReport
                    ? 'Finish Semi-Structured Interview'
                    : 'Proceed to Diagnostic Decision'}
              </span>
              {isSaving
                ? <ButtonSpinner className="w-5 h-5 ml-2" />
                : <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
            </button>
          )}
          {/* Lead-override finisher CTA (see top nav row for rationale). */}
          {isLeadOverrideFinisher && (
            <button
              onClick={() => setLeadFinishDialogOpen(true)}
              className="flex items-center py-2 px-4 rounded-md shadow-md transition-all duration-300 hover:bg-violet-800 bg-violet-900 text-white"
            >
              <span className="font-bold">Finish Semi-Structured Interview</span>
              <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
            </button>
          )}
        </div>
       </div>
      </div>

      {/* Appointment finish wizard (one-clinician SLaM / 3 Borough).
          The SSI Review is the last interview of the appointment, so Complete
          asks: share the outcome now and start the PAD (Path 1), or end the
          appointment and generate the SSI report (Path 2). Same component and
          decision key as the CI Review entry point for two-clinician patients. */}
      {asksAppointmentFinishPath && (
        <InterviewFinishPathDialog
          open={isDialogOpen && !isGenerating}
          onOpenChange={setIsDialogOpen}
          moduleLabel="Semi-Structured Interview"
          differentials={differentials.selected.filter(c => c !== ADHD_CONDITION_LABEL)}
          formulation={formulation}
          isSubmitting={isFinishPathSubmitting}
          onConfirmPadPath={handleConfirmSsiPadPath}
          onGenerateReport={handleFinishSSIDeferPad}
        />
      )}

      {/* Completion dialog.
          SLaM / 3 Borough (hasCIReview): symptom/domain summary + Generate Report
          → progress dialog → Documents (SSI clinician does not open DD).
          MDT-review orgs: emerging differentials recap + Generate Report → Documents.
          Other Review-page orgs: proceed to Diagnostic Decision. */}
      <ModuleCompletionDialog
        open={isDialogOpen && !isGenerating && !asksAppointmentFinishPath}
        onOpenChange={setIsDialogOpen}
        title={finishesSSIWithReport
          ? 'Semi-Structured Interview Complete'
          : 'Clinical Review Complete'}
        description={
          wf.hasCIReview
            ? <>Review the decision counts below, then generate the Semi-Structured Interview report for approval.</>
            : wf.isMDTReview
              ? <>Your <span className="font-semibold">Clinical Interview</span> and <span className="font-semibold">Semi-Structured Interview</span> reports are ready for review.</>
              : <>Review your emerging differentials before proceeding to the <span className="font-semibold">Diagnostic Decision</span>.</>
        }
        actions={
          <>
            <CancelButton onClick={() => setIsDialogOpen(false)} />
            {finishesSSIWithReport ? (
              <GenerateReportButton onClick={handleFinishSSI} />
            ) : (
              <PrimaryActionButton
                label="Proceed to Diagnostic Decision"
                onClick={handleFinishSSI}
              />
            )}
          </>
        }
      >
        {wf.hasCIReview ? (
          <SummarySection title="Decision summary:">
            <SSIDecisionCountSummary
              attentionTotals={attentionTotals}
              hyperactivityTotals={hyperactivityTotals}
              domainImpairmentData={domainImpairmentData}
            />
          </SummarySection>
        ) : (
          <>
            <SummarySection title="Condition referred for:">
              <p className="text-sm text-gray-700">{ADHD_CONDITION_LABEL}</p>
            </SummarySection>

            <SummarySection title="Emerging differentials under consideration:">
              {constructDifferentialsRecap()}
            </SummarySection>

            <SummarySection title="Emerging formulation:">
              {formulation.trim() ? (
                <div className="text-gray-700 text-sm whitespace-pre-wrap max-h-32 overflow-y-auto bg-gray-50 rounded-lg p-3 border border-gray-200">
                  {formulation}
                </div>
              ) : (
                <p className="text-sm italic text-gray-400">Not provided</p>
              )}
            </SummarySection>

            <SummarySection title="Confidential notes:">
              {caseNotes.trim() ? (
                <div className="text-gray-700 text-sm whitespace-pre-wrap max-h-32 overflow-y-auto bg-gray-50 rounded-lg p-3 border border-gray-200">
                  {caseNotes}
                </div>
              ) : (
                <p className="text-sm italic text-gray-400">Not provided</p>
              )}
            </SummarySection>
          </>
        )}

        <DialogMessage>
          {wf.hasCIReview
            ? 'After the report is ready, you will review and approve the Semi-Structured Interview paragraphs on the Documents page.'
            : wf.isMDTReview
              ? 'Your formulation notes and emerging differentials will be available as a reference during the MDT review.'
              : <>Your formulation notes will be available as a reference during the <span className="font-semibold not-italic">Diagnostic Decision</span>.</>}
        </DialogMessage>
      </ModuleCompletionDialog>

      {/* Lead-override finish dialog.
          Shown only when the Lead is finishing the SSI they have taken over.
          The primary CTA depends on where the Lead is in their own workflow:
            - DD already complete  → "Generate Report": run the simulated
              progress dialog, then land on the Documents Dashboard where the SSI
              report section is waiting for review.
            - DD not yet complete  → "Go back to Diagnostic Decision": return to
              the DD to reaffirm the decision against the updated counts.
          Cancel simply stays on the page (the enforced-exit guard remains armed,
          so the choice is re-prompted on the next managed exit). */}
      {isLeadOverrideFinisher && (
        <ModuleCompletionDialog
          open={leadFinishDialogOpen && !isGenerating}
          onOpenChange={setLeadFinishDialogOpen}
          title="Semi-Structured Interview Complete"
          description={
            isDDCompleted(patientDataModel)
              ? <>Review the decision counts below, then generate the updated Semi-Structured Interview report for approval.</>
              : <>Review the decision counts below, then return to the <span className="font-semibold">Diagnostic Decision</span> to confirm your decision against the updated counts.</>
          }
          actions={
            <>
              <CancelButton onClick={() => setLeadFinishDialogOpen(false)} />
              {isDDCompleted(patientDataModel) ? (
                <GenerateReportButton onClick={() => handleLeadFinishSSI('documents')} />
              ) : (
                <PrimaryActionButton
                  label="Go back to Diagnostic Decision"
                  onClick={() => handleLeadFinishSSI('dd')}
                />
              )}
            </>
          }
        >
          <SummarySection title="Decision summary:">
            <SSIDecisionCountSummary
              attentionTotals={attentionTotals}
              hyperactivityTotals={hyperactivityTotals}
              domainImpairmentData={domainImpairmentData}
            />
          </SummarySection>

          <DialogMessage>
            {isDDCompleted(patientDataModel)
              ? 'Any Semi-Structured Interview paragraphs affected by your changes will be regenerated for you to review and approve on the Documents page before the Patient Report can be finalised.'
              : 'Your updated symptom and domain counts will be reflected on the Diagnostic Decision page.'}
          </DialogMessage>
        </ModuleCompletionDialog>
      )}

      {/* Simulated report progress, then Documents (SSI approval). */}
      <GenerateReportProgressDialog
        reportStage="Semi-Structured Interview"
        patientId={patientId}
        clinicianId={clinicianId}
        handleNextPageButtonClick={handleAfterGeneration}
        isGenerating={isGenerating}
        onCancel={handleCancelGeneration}
      />
    </Layout>
  );
}
